import ApiInit from "./ApiInit";
import Formatter from "./Formatter";

// CRUD-модель поверх ApiInit. Наследники задают pathDef (+ опц. paths, formatterItem).
// Использование: const api = Api.Show(id); const data = await api.getResponse();
// после — api.code, api.errors, api.paginationCurrent/paginationLast/paginationTotal.
export default class ApiModel extends ApiInit {
    pathDef = '';                       // базовый путь ресурса, напр. 'authorized/admin/nodes'
    paths = {};                         // опц. переопределение пути на операцию {index, store, show, update, destroy}
    __type = null;                      // 'index'|'show'|'store'|'update'|'destroy'|'group'
    __responseIsArray = false;          // ответ — массив записей
    formatterItem = Formatter.Item;     // форматтер одной записи (null — не форматировать)
    formatterResponseData = null;       // опц. трансформация response.data целиком
    isCheckPagination = false;          // разбирать response.meta.{current_page,last_page}
    isSendFile = false;                 // тело — FormData (см. ApiModelWithAuth.getHeaders)
    __showErrors = true;                // показывать ошибки тостами

    code = null;                        // HTTP-код последнего запроса (-1 — сеть упала)
    errors = null;                      // {field: [messages]} из ответа валидации
    paginationCurrent = null;
    paginationLast = null;
    paginationTotal = null;             // всего записей по фильтру (meta.total), null — ответ без пагинации

    // --- Статические фабрики -------------------------------------------------

    static Index(params = {}) {
        const api = new this();
        api.__type = 'index';
        api.__responseIsArray = true;
        api.isCheckPagination = true;
        api.__params = params;
        return api;
    }

    static Show(id) {
        const api = new this();
        api.__type = 'show';
        api.__id = id;
        return api;
    }

    static Store(params = {}) {
        const api = new this();
        api.__type = 'store';
        api.__params = params;
        return api;
    }

    static Update(id, params = {}) {
        const api = new this();
        api.__type = 'update';
        api.__id = id;
        api.__params = params;
        return api;
    }

    static Destroy(id) {
        const api = new this();
        api.__type = 'destroy';
        api.__id = id;
        return api;
    }

    static Group(key) {
        const api = new this();
        api.__type = 'group';
        api.__key = key;
        return api;
    }

    // --- Хуки для наследников ------------------------------------------------

    getParamsForStore(params) {
        return params;
    }

    getParamsForUpdate(params) {
        return params;
    }

    // Клиентская валидация перед store/update: вернуть массив текстов ошибок.
    _validation(params) {
        return [];
    }

    // --- Выполнение ----------------------------------------------------------

    getPath(slug) {
        return (slug in this.paths) ? this.paths[slug] : this.pathDef;
    }

    // Прослойка между getResponse и __query — ApiModelWithAuth добавляет сюда Bearer + retry.
    async _query(path, method, data, type) {
        return this.__query(path, method, data, type);
    }

    async getResponse() {
        if (this.__type === 'store' || this.__type === 'update') {
            const validationErrors = this._validation(this.__params ?? {});
            if (validationErrors.length > 0) {
                validationErrors.forEach((message) => this.addMessages(message, 'error'));
                this.code = 422;
                return null;
            }
        }

        // Ресурсы-одиночки (профиль) работают без id — сегмент не добавляется
        const idSegment = (this.__id === undefined || this.__id === null) ? '' : `/${this.__id}`;
        let path = this.getPath(this.__type);
        let method = 'GET';
        let data = null;
        switch (this.__type) {
            case 'index':
                data = this.__params;
                break;
            case 'show':
                path = `${path}${idSegment}`;
                break;
            case 'store':
                method = 'POST';
                data = this.getParamsForStore(this.__params);
                break;
            case 'update':
                method = 'PATCH';
                path = `${path}${idSegment}`;
                data = this.getParamsForUpdate(this.__params);
                break;
            case 'destroy':
                method = 'DELETE';
                path = `${path}${idSegment}`;
                break;
            case 'group':
                path = `${path}/group/${this.__key}`;
                break;
            default:
                break;
        }

        const answer = await this._query(path, method, data);
        this.code = answer.code;

        if (!answer.result) {
            this.handleError(answer);
            return null;
        }
        if (this.code >= 201 && this.code <= 204) this.addMessages('Успешно', 'success');
        return this.parseResponse(answer.response);
    }

    // Следующая страница списка (для догрузки).
    async getResponseNext() {
        this.__params = { ...(this.__params ?? {}), page: (this.paginationCurrent ?? 1) + 1 };
        return this.getResponse();
    }

    // Форма ответа Laravel Resources: список {data: [], meta: {}}, запись {data: {}} либо плоско.
    parseResponse(response) {
        if (response === null || response === undefined) return null; // 204 без тела
        this.checkPagination(response);
        let data = (typeof response === 'object' && 'data' in response) ? response.data : response;
        if (this.formatterResponseData) data = this.formatterResponseData(data);
        if (this.__responseIsArray) {
            if (!Array.isArray(data)) data = [];
            return this.formatterItem ? data.map(this.formatterItem) : data;
        }
        if (this.formatterItem && data && typeof data === 'object' && !Array.isArray(data)) {
            return this.formatterItem(data);
        }
        return data;
    }

    checkPagination(response) {
        if (!this.isCheckPagination || !response || typeof response !== 'object') return;
        // Пагинация в response.meta либо на верхнем уровне
        const meta = (response.meta && typeof response.meta === 'object') ? response.meta : response;
        if (meta.current_page === undefined) return;
        this.paginationCurrent = Formatter.Int(meta.current_page);
        this.paginationLast = Formatter.Int(meta.last_page);
        this.paginationTotal = Formatter.Int(meta.total);
    }

    handleError(answer) {
        if (answer.code === -1) {
            this.addMessages('Отсутствует подключение к серверу', 'error');
            return;
        }
        const response = answer.response;
        this.errors = (response && response.errors) ? response.errors : null;
        if (!this.__showErrors) return;
        // 401/403/404 обрабатываются выше (refresh/Logout, страницы Abort) — без тостов
        if (answer.code === 401 || answer.code === 403 || answer.code === 404) return;
        if (this.errors) {
            for (const field in this.errors) {
                const messages = Array.isArray(this.errors[field]) ? this.errors[field] : [this.errors[field]];
                messages.forEach((message) => this.addMessages(message, 'error'));
            }
            return;
        }
        if (response && (response.message || response.error)) {
            this.addMessages(response.message || response.error, 'error');
            return;
        }
        this.addMessages(`Ошибка запроса (код ${answer.code})`, 'error');
    }
}
